import { Formik } from 'formik';
import { useMemo } from 'react';

import { Messages } from 'config';
import { formatPrice } from 'utils/helpers';
import { useChangeStatusMutation } from 'store/api/payout';
import { isMobile, useNavigate, handleMutation } from 'hooks';
import { PayoutType, PayoutStatus, ButtonColors } from 'types';
import {
  Flex,
  Icon,
  Dialog,
  Button,
  InputField,
  DialogType,
  ButtonTypes,
  CommonStyles,
  ButtonWithRef,
} from 'components';

import { ApprovalPayoutDialogProps } from './types';
import { initialValues, validationSchema } from './config';
import { PartialApprovedDialog } from './partial-approved';

export const ApprovalPayoutDialog = ({
  data,
  withNavigate,
  hidePartialApproved,
  type = PayoutType.Reimbursements,
}: ApprovalPayoutDialogProps) => {
  const mobile = isMobile();
  const navigate = useNavigate();
  const lowerType = useMemo(() => type.toLowerCase(), [type]);

  const [changeStatus, mutation] = useChangeStatusMutation();

  handleMutation({
    ...mutation,
    successMessage: `${type} status ${Messages.UPDATED_SUCCESSFULLY}`,
  });

  return (
    <Flex gap={16}>
      <Dialog
        title="Approve"
        closeOnDocumentClick={false}
        confirmStyle={ButtonColors.Green}
        trigger={
          <ButtonWithRef
            tooltip="Approve"
            fullWidth={mobile}
            icon={<Icon.CheckCircle />}
            colorType={ButtonColors.LightGreen}
          />
        }
        onConfirm={async () => {
          await changeStatus({
            id: data.id,
            status: PayoutStatus.Approved,
          }).unwrap();
          if (withNavigate) {
            navigate(-1);
          }
        }}
        text={
          <>
            You're going to approve this {lowerType} at the event{' '}
            <span className="semibold">{data.event?.name}</span> and with amount{' '}
            <span className="semibold">
              {formatPrice(data.amount, '$0.00')}
            </span>
            . Are you sure you want to do this?
          </>
        }
      />

      {!hidePartialApproved && (
        <PartialApprovedDialog
          data={data}
          type={lowerType}
          withNavigate={withNavigate}
          onChangeStatus={async (amount) => {
            const payout = await changeStatus({
              amount,
              id: data.id,
              status: PayoutStatus.PartialApproved,
            }).unwrap();

            return payout;
          }}
        />
      )}

      <Dialog
        closeOnDocumentClick={false}
        title={`Reject ${lowerType}`}
        trigger={
          <ButtonWithRef
            tooltip="Reject"
            fullWidth={mobile}
            colorType={ButtonColors.LightRed}
            icon={<Icon.CloseCircleNotFilled />}
          />
        }
      >
        {(close, _, ButtonWrap) => (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async ({ reasonReject }) => {
              const payout = await changeStatus({
                id: data.id,
                reasonReject,
                status: PayoutStatus.NotApproved,
              });
              if (payout) {
                if (withNavigate) {
                  navigate(-1);
                }
                close();
              }
            }}
          >
            {({ dirty, isSubmitting }) => (
              <CommonStyles.Form gap={24}>
                <InputField
                  type="textarea"
                  controlErrorLabel
                  name="reasonReject"
                  label="Reason reject"
                  placeholder="Add reject reason"
                />

                <ButtonWrap type={DialogType.Plain}>
                  <Button
                    text="Cancel"
                    onClick={close}
                    colorType={ButtonColors.LightBlue}
                  />
                  <Button
                    text="Reject"
                    type={ButtonTypes.Submit}
                    colorType={ButtonColors.LightRed}
                    disabled={!dirty || isSubmitting}
                  />
                </ButtonWrap>
              </CommonStyles.Form>
            )}
          </Formik>
        )}
      </Dialog>
    </Flex>
  );
};
